import { HTMLAttributes, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper as SwiperType } from "swiper";
import { Mousewheel, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Collection, FullItems } from "@/api";
import {
  AppCTAModal,
  Button,
  ContentThumbnail,
  ContentTitle,
  DripLockedModal,
  Dropdown,
} from "@/components";
import { DropdownOption } from "@/components/Forms/Dropdown.tsx";
import { ITEM_TYPE_COLLECTION } from "@/const.ts";
import {
  useAppCTAModal,
  useCurrentTabId,
  useFullItems,
  useNavigateToItem,
} from "@/hooks";
import { ChevronLeftMD, ChevronRightMD, Ellipsis } from "@/icons";
import { useResponsiveness, useTranslate, useUser } from "@/providers/context";
import {
  cn,
  getContentThumbnailUrl,
  getContentTitle,
  itemRequiresApp,
} from "@/utils";

export interface SliderProps extends HTMLAttributes<HTMLDivElement> {
  collection: Pick<Collection, "Items" | "Name" | "TabId">;
  fullItems: FullItems;
  playlistId?: number; // When its a playlist

  "data-testid"?: string;
  className?: string;
  dropdownOptions?: DropdownOption[]; // Optional Ellipsis dropdown menu
  orientation?: "landscape" | "portrait" | "square";
  size?: "sm" | "lg";
}

export const Slider = ({
  collection,
  fullItems,
  playlistId,
  dropdownOptions,
  orientation = "landscape",
  size = "sm",
  ...props
}: SliderProps) => {
  const { tabId } = useCurrentTabId();
  const { navigateToItem } = useNavigateToItem();
  const { colsPerRow } = useResponsiveness();
  const { getFullItem } = useFullItems(fullItems);
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const { modalOpen, modalItem, openAppCTAModal, setModalOpen } =
    useAppCTAModal();
  const { isCollectionDripLocked } = useUser();
  const [dripLockedModalOpen, setDripLockedModalOpen] = useState(false);

  const swiperRef = useRef<SwiperType>();

  const adjustedColsPerRow = useMemo(() => {
    if (orientation === "landscape") {
      return size === "lg" ? Math.floor(colsPerRow / 2) : colsPerRow;
    }
    if (orientation === "portrait" || orientation === "square") {
      return colsPerRow + 1;
    }
    return colsPerRow;
  }, [orientation, colsPerRow, size]);

  if (collection?.Items?.length === 0) {
    console.debug("Slider hidden due to no supported items", { tabId });
    return null;
  }
  const items = collection?.Items;

  return (
    <div {...props}>
      <div className="mb-[12px] flex h-[38px] items-center justify-between">
        <div
          data-testid={`${props["data-testid"]}-title`}
          className="h5-bold flex-1 items-center overflow-hidden overflow-ellipsis whitespace-nowrap text-item-primary"
        >
          {collection.Name}
        </div>
        {(items.length > adjustedColsPerRow || !!playlistId) && (
          <div className="flex items-center">
            {dropdownOptions && (
              <Dropdown
                options={dropdownOptions}
                contentProps={{ align: "end" }}
              >
                <Ellipsis className="h-6 w-6" />
              </Dropdown>
            )}
            <Button
              size="m"
              variant="tertiary"
              className="p2-semibold capitalize"
              onClick={() => {
                playlistId
                  ? navigate(`/playlist/${playlistId}`)
                  : navigateToItem({
                      item: {
                        ChildId: collection.TabId.toString(),
                        Type: ITEM_TYPE_COLLECTION,
                      },
                    });
              }}
            >
              {translate("View All")}
            </Button>
            <button
              className="ml-[8px] flex h-[24px] w-[24px] items-center justify-center rounded-[50%] bg-surface-tertiary hover:bg-border"
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <ChevronLeftMD className="h-[20px] w-[20px]" />
            </button>
            <button
              className="ml-[8px] flex h-[24px]  w-[24px] items-center justify-center rounded-[50%] bg-surface-tertiary hover:bg-border"
              onClick={() => swiperRef.current?.slideNext()}
            >
              <ChevronRightMD className="h-[20px] w-[20px]" />
            </button>
          </div>
        )}
      </div>
      <Swiper
        slidesPerView={adjustedColsPerRow}
        slidesPerGroup={adjustedColsPerRow}
        spaceBetween={16}
        modules={[Mousewheel, Virtual]}
        mousewheel={{ forceToAxis: true }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
        virtual={!!(items?.length && items?.length > 50)}
      >
        {items?.map((i, index) => {
          const fullItem = getFullItem(i);
          const showAppCTAModal = itemRequiresApp(fullItem);
          const isDripLocked =
            fullItem && "TabId" in fullItem
              ? isCollectionDripLocked(fullItem as Collection)
              : false;

          return (
            <SwiperSlide
              onClick={() => {
                if (showAppCTAModal && fullItem) {
                  openAppCTAModal(fullItem);
                  return;
                }

                if (isDripLocked) {
                  setDripLockedModalOpen(true);
                  return;
                }

                navigateToItem({
                  parentId: !playlistId ? collection.TabId : undefined,
                  item: i,
                  fullItem,
                });
              }}
              key={`${index}|${i.ChildId}`}
              virtualIndex={index}
            >
              <ContentThumbnail
                thumbnail={
                  fullItem
                    ? getContentThumbnailUrl(
                        fullItem,
                        size === "lg" ? "large" : "medium",
                        orientation,
                      )
                    : ""
                }
                contentItem={fullItem}
                className={cn([
                  "w-full  cursor-pointer rounded-[8px]",
                  orientation === "landscape" && "aspect-video",
                  orientation === "portrait" && "aspect-[2/3]",
                  orientation === "square" && "aspect-square",
                ])}
                isDripLocked={isDripLocked}
              />
              <ContentTitle title={fullItem ? getContentTitle(fullItem) : ""} />
            </SwiperSlide>
          );
        })}
      </Swiper>
      <AppCTAModal open={modalOpen} setOpen={setModalOpen} item={modalItem} />
      <DripLockedModal
        open={dripLockedModalOpen}
        setOpen={setDripLockedModalOpen}
      />
    </div>
  );
};
